import ResourcesDispatcher from '../config/ResourcesDispatcher';
// ============================================================================
// CURRENCY ENUMS
// ============================================================================
var CurrencyText;
(function (CurrencyText) {
    CurrencyText["EUR"] = "eur";
    CurrencyText["GBP"] = "gbp";
    CurrencyText["SEK"] = "sek";
    CurrencyText["USD"] = "usd";
    CurrencyText["AOA"] = "aoa";
    CurrencyText["GTQ"] = "gtq";
    CurrencyText["CHF"] = "chf";
    CurrencyText["NOC"] = "noc";
    CurrencyText["AED"] = "aed";
    CurrencyText["MXN"] = "mxn";
    CurrencyText["FUN"] = "fun";
    CurrencyText["BNS"] = "bns";
})(CurrencyText || (CurrencyText = {}));
var CurrencySymbol;
(function (CurrencySymbol) {
    CurrencySymbol["EUR"] = "\u20AC";
    CurrencySymbol["GBP"] = "\u00A3";
    CurrencySymbol["SEK"] = "kr";
    CurrencySymbol["USD"] = "$";
    CurrencySymbol["AOA"] = "kz";
    CurrencySymbol["GTQ"] = "Q";
    CurrencySymbol["CHF"] = "CHF";
    CurrencySymbol["NOC"] = "";
    CurrencySymbol["AED"] = "DH";
    CurrencySymbol["MXN"] = "$";
    CurrencySymbol["FUN"] = "FUN";
    CurrencySymbol["BNS"] = "BNS";
})(CurrencySymbol || (CurrencySymbol = {}));
const CURRENCY_MAP = {
    [CurrencyText.EUR]: { symbol: CurrencySymbol.EUR, divider: 100 },
    [CurrencyText.GBP]: { symbol: CurrencySymbol.GBP, divider: 100 },
    [CurrencyText.SEK]: { symbol: CurrencySymbol.SEK, divider: 10 },
    [CurrencyText.USD]: { symbol: CurrencySymbol.USD, divider: 100 },
    [CurrencyText.AOA]: { symbol: CurrencySymbol.AOA, divider: 100 },
    [CurrencyText.GTQ]: { symbol: CurrencySymbol.GTQ, divider: 100 },
    [CurrencyText.CHF]: { symbol: CurrencySymbol.CHF, divider: 100 },
    [CurrencyText.NOC]: { symbol: CurrencySymbol.NOC, divider: 100 },
    [CurrencyText.AED]: { symbol: CurrencySymbol.AED, divider: 100 },
    [CurrencyText.MXN]: { symbol: CurrencySymbol.MXN, divider: 100 },
    [CurrencyText.FUN]: { symbol: CurrencySymbol.FUN, divider: 100 },
    [CurrencyText.BNS]: { symbol: CurrencySymbol.BNS, divider: 100 }
};
// ============================================================================
// ANIMATION CONFIGURATION
// ============================================================================
const SPIN_BUTTON_CONFIG = {
    LOOP_DURATION: 450,
    STOP_DURATION: 750,
    ANGLE: 360,
    EASE_LOOP: 'Linear',
    EASE_STOP: 'Cubic.easeOut',
};
// ============================================================================
// UTILS CLASS
// ============================================================================
export default class Utils {
    /**
     * Waits for a specified time using Phaser's timer system
     */
    static wait(time, scene) {
        return new Promise((resolve) => {
            scene.time.addEvent({
                delay: time,
                callback: () => resolve()
            });
        });
    }
    /**
    * Checks if an array contains a specific value using strict equality
    */
    static includes(array, value) {
        for (let i = 0; i < array.length; i++) {
            if (array[i] === value) {
                return true;
            }
        }
        return false;
    }
    /**
     * Returns a random integer between min (inclusive) and max (inclusive)
     */
    static getRandomInt(min, max) {
        const minCeil = Math.ceil(min);
        const maxFloor = Math.floor(max);
        return Math.floor(Math.random() * (maxFloor - minCeil + 1)) + minCeil;
    }
    /**
     * Generates cell positions (3 visible + 2 extra cells)
     */
    static generateCellPositions(numCells, cellSize) {
        const positions = [];
        const halfW = cellSize.width * 0.5;
        const halfH = cellSize.height * 0.5;
        for (let i = 0; i < numCells; i++) {
            const y = cellSize.height * (numCells - 2 - i) + halfH;
            positions.push({ x: halfW, y });
        }
        return positions;
    }
    /**
     * Gets the grid index for a cell, excluding extra cells (returns null for extra cells)
     */
    static getCellGridIndex(reelIndex, cellIndex, numOfCellsPerReel, numOfExtraCellsPerReel) {
        const isExtraCell = cellIndex === 0 || cellIndex === numOfCellsPerReel + numOfExtraCellsPerReel - 1;
        if (isExtraCell) {
            return null;
        }
        return reelIndex * numOfCellsPerReel + cellIndex - 1;
    }
    static formatValue(x) {
        return window.FORMATTER.formatCurrency(x);
    }
    /**
     * Formats a currency value with symbol and thousands separator
     * @TODO Make currency dynamic instead of hardcoded EUR
     */
    static formatCurrency(value, useSymbol = true, callbackFormatAmount) {
        const currencyKey = CurrencyText.EUR; // TODO: Make this dynamic
        const { symbol, divider } = CURRENCY_MAP[currencyKey];
        const amount = value / divider;
        if (typeof callbackFormatAmount === 'function') {
            return callbackFormatAmount(amount);
        }
        const formatted = this.formatNumber(amount);
        return useSymbol ? `${symbol} ${formatted}` : formatted;
    }
    /**
     * Formats a number with thousands separator and decimal places
     */
    static formatNumber(value, decimals = 2) {
        return value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    /**
     * Creates a rotation animation for the main spin button
     */
    static spinMainButtonRotation(mainButton, scene) {
        const isFreeSpinPlaying = window.isFreeSpinEntryPlaying;
        if (!isFreeSpinPlaying) {
            scene.soundManager.play(ResourcesDispatcher.SOUNDS.SPIN_MAIN_BUTTON);
        }
        return scene.tweens.add({
            targets: mainButton,
            angle: SPIN_BUTTON_CONFIG.ANGLE,
            duration: SPIN_BUTTON_CONFIG.LOOP_DURATION,
            repeat: -1,
            ease: SPIN_BUTTON_CONFIG.EASE_LOOP,
            onRepeat: () => {
                mainButton.angle = 0;
            },
            onStop: () => {
                this.createStopAnimation(mainButton, scene);
            }
        });
    }
    /**
     * Creates the stop animation for the spin button
     */
    static createStopAnimation(mainButton, scene) {
        scene.tweens.add({
            targets: mainButton,
            angle: SPIN_BUTTON_CONFIG.ANGLE,
            duration: SPIN_BUTTON_CONFIG.STOP_DURATION,
            ease: SPIN_BUTTON_CONFIG.EASE_STOP,
            onComplete: () => {
                scene.soundManager.stopFade(ResourcesDispatcher.SOUNDS.SPIN_MAIN_BUTTON);
            }
        });
    }
}
